import styled, { keyframes, css } from 'styled-components'
import { Themes } from '@src/styles/theme'

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(30px); }
`

const floatReverse = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-30px); }
`

const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const spin = keyframes`
  to { transform: rotate(360deg); }
`

// Background gradients
export const BgGradient1 = styled.div`
  position: fixed;
  top: -40%;
  right: -20%;
  width: 800px;
  height: 800px;
  background: radial-gradient(circle, rgba(51, 170, 64, 0.12) 0%, transparent 70%);
  border-radius: 50%;
  animation: ${float} 20s ease-in-out infinite;
  pointer-events: none;
  z-index: 1;
`

export const BgGradient2 = styled.div`
  position: fixed;
  bottom: -30%;
  left: -10%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(50, 146, 192, 0.08) 0%, transparent 70%);
  border-radius: 50%;
  animation: ${floatReverse} 25s ease-in-out infinite;
  pointer-events: none;
  z-index: 1;
`

// Page container - uses theme colors
export const LandingContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.selected === Themes.LIGHT
    ? 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #dee2e6 100%)'
    : `linear-gradient(135deg, ${props.theme.header} 0%, ${props.theme.secondary} 50%, ${props.theme.tertiary} 100%)`};
  color: ${props => props.theme.white};
  overflow-x: hidden;
  font-family: 'NunitoSans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  line-height: 1.5;
`

// Header
export const Header = styled.header`
  position: relative;
  z-index: 10;
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${props => props.theme.selected === Themes.LIGHT
    ? 'rgba(255, 255, 255, 0.9)'
    : 'rgba(31, 49, 58, 0.8)'};
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${props => props.theme.grayDivider};
`

export const HeaderBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    width: 32px;
    height: 32px;
  }
`

export const HeaderBrandText = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.label};
  letter-spacing: -0.02em;
`

export const HeaderNav = styled.nav`
  display: flex;
  gap: 2rem;
  align-items: center;

  a {
    font-size: 0.875rem;
    color: ${props => props.theme.breadcrumb};
    text-decoration: none;
    transition: color 0.3s ease;
    font-weight: 500;

    &:hover {
      color: ${props => props.theme.accent};
    }
  }

  @media (max-width: 768px) {
    gap: 1rem;

    a {
      display: none;
    }
  }
`

export const SignInButton = styled.button`
  padding: 0.625rem 1.5rem;
  border: 1px solid ${props => props.theme.grayDivider};
  background: transparent;
  color: ${props => props.theme.label};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s ease;
  font-family: inherit;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    border-color: ${props => props.theme.accent};
    color: ${props => props.theme.accent};
    background: rgba(51, 170, 64, 0.1);
  }
`

// Main content
export const Main = styled.main`
  position: relative;
  z-index: 5;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 80px);
  padding: 2rem;
`

export const HeroText = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  max-width: 800px;
  animation: ${fadeInDown} 0.8s ease-out;

  h1 {
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1.25rem;
    line-height: 1.1;
    letter-spacing: -0.02em;

    @media (max-width: 768px) {
      font-size: 2.25rem;
    }
  }

  p {
    font-size: 1.25rem;
    color: ${props => props.theme.breadcrumb};
    margin-bottom: 0.5rem;
    line-height: 1.6;

    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`

export const Highlight = styled.span`
  background: linear-gradient(135deg, ${props => props.theme.accent} 0%, ${props => props.theme.button?.primary?.hover || '#0E7923'} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`

export const Subtitle = styled.p`
  font-size: 1rem !important;
  color: ${props => props.theme.disabled} !important;
`

// Input container
export const InputContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 700px;
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
`

export const InputWrapper = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: stretch;
  background: ${props => props.theme.selected === Themes.LIGHT
    ? 'rgba(255, 255, 255, 0.95)'
    : 'rgba(43, 59, 68, 0.9)'};
  backdrop-filter: blur(10px);
  border: 1px solid ${props => props.theme.grayDivider};
  border-radius: 8px;
  padding: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: ${props => props.theme.selected === Themes.LIGHT
    ? '0 20px 40px rgba(0, 0, 0, 0.1)'
    : '0 20px 40px rgba(0, 0, 0, 0.3)'};

  &:focus-within {
    border-color: ${props => props.theme.accent};
    box-shadow: 0 0 0 3px rgba(51, 170, 64, 0.2), 0 20px 60px rgba(0, 0, 0, 0.4);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1rem;
  }
`

export const InputField = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: ${props => props.theme.label};
  font-size: 1rem;
  outline: none;
  padding: 1rem;
  font-family: inherit;

  &::placeholder {
    color: ${props => props.theme.disabled};
  }
`

export const SubmitButton = styled.button`
  background: ${props => props.theme.accent};
  color: #ffffff;
  border: none;
  padding: 1rem 2rem;
  border-radius: 4px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  font-size: 0.875rem;
  font-family: inherit;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    background: ${props => props.theme.button?.primary?.hover || '#0E7923'};
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(51, 170, 64, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`

// Example prompts
export const ExamplePrompts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.8s ease-out 0.3s both;
`

export const ExamplePrompt = styled.button`
  background: ${props => props.theme.selected === Themes.LIGHT
    ? 'rgba(0, 0, 0, 0.05)'
    : 'rgba(43, 59, 68, 0.6)'};
  border: 1px solid ${props => props.theme.grayDivider};
  color: ${props => props.theme.breadcrumb};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;

  &:hover {
    background: rgba(51, 170, 64, 0.15);
    border-color: rgba(51, 170, 64, 0.4);
    color: ${props => props.theme.accent};
  }
`

// Bottom text
export const BottomText = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: ${props => props.theme.disabled};
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;

  a {
    color: ${props => props.theme.accent};
    text-decoration: none;
    cursor: pointer;
    transition: color 0.3s ease;

    &:hover {
      color: ${props => props.theme.button?.primary?.hover || '#0E7923'};
      text-decoration: underline;
    }
  }
`

// Features section
export const Features = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 4rem;
  animation: ${fadeInUp} 0.8s ease-out 0.5s both;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

export const Feature = styled.div`
  text-align: center;
  padding: 1.5rem;

  h3 {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: ${props => props.theme.label};
  }

  p {
    font-size: 0.875rem;
    color: ${props => props.theme.disabled};
  }
`

export const FeatureIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto 1rem;
  background: rgba(51, 170, 64, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
    color: ${props => props.theme.accent};
  }
`

// Modal styles
export const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 100;
  align-items: center;
  justify-content: center;
`

export const ModalContent = styled.div`
  background: ${props => props.theme.selected === Themes.LIGHT
    ? 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
    : `linear-gradient(135deg, ${props.theme.tertiary} 0%, ${props.theme.secondary} 100%)`};
  border: 1px solid ${props => props.theme.grayDivider};
  border-radius: 8px;
  padding: 2.5rem;
  max-width: 480px;
  width: 90%;
  animation: ${slideUp} 0.4s ease-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`

export const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`

export const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.label};
`

export const ModalSubtitle = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.breadcrumb};
`

export const ContextHighlight = styled.div`
  background: rgba(51, 170, 64, 0.15);
  border: 1px solid rgba(51, 170, 64, 0.3);
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`

export const ContextLabel = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.accent};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
`

export const ContextText = styled.div`
  font-size: 0.95rem;
  color: ${props => props.theme.label};
  font-weight: 500;
`

export const AuthButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`

export const AuthButton = styled.button<{ primary?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem;
  border-radius: 4px;
  border: 1px solid ${props => props.theme.grayDivider};
  background: ${props => props.theme.selected === Themes.LIGHT
    ? 'rgba(0, 0, 0, 0.03)'
    : 'rgba(43, 59, 68, 0.5)'};
  color: ${props => props.theme.label};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  font-family: inherit;

  &:hover {
    background: rgba(51, 170, 64, 0.1);
    border-color: rgba(51, 170, 64, 0.4);
  }

  ${props => props.primary && css`
    background: ${props.theme.accent};
    border: none;
    color: #ffffff;

    &:hover {
      background: ${props.theme.button?.primary?.hover || '#0E7923'};
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(51, 170, 64, 0.3);
    }
  `}
`

export const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1.5rem 0;
  color: ${props => props.theme.disabled};
  font-size: 0.875rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${props => props.theme.grayDivider};
  }
`

export const SignInLink = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: ${props => props.theme.breadcrumb};

  a {
    color: ${props => props.theme.accent};
    cursor: pointer;
    text-decoration: none;

    &:hover {
      color: ${props => props.theme.button?.primary?.hover || '#0E7923'};
      text-decoration: underline;
    }
  }
`

// Form styles
export const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`

export const FormLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.breadcrumb};
`

export const FormInput = styled.input`
  width: 100%;
  background: ${props => props.theme.selected === Themes.LIGHT
    ? 'rgba(255, 255, 255, 0.9)'
    : 'rgba(30, 40, 50, 0.6)'};
  border: 1px solid ${props => props.theme.grayDivider};
  border-radius: 4px;
  padding: 0.875rem 1rem;
  color: ${props => props.theme.label};
  font-size: 0.95rem;
  transition: all 0.3s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.accent};
    box-shadow: 0 0 0 3px rgba(51, 170, 64, 0.15);
  }

  &::placeholder {
    color: ${props => props.theme.disabled};
  }
`

export const SubmitButtonModal = styled.button`
  width: 100%;
  background: ${props => props.theme.accent};
  color: #ffffff;
  border: none;
  padding: 0.875rem;
  border-radius: 4px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  font-family: inherit;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    background: ${props => props.theme.button?.primary?.hover || '#0E7923'};
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(51, 170, 64, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: none;
  color: ${props => props.theme.accent};
  cursor: pointer;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  transition: color 0.3s ease;
  font-family: inherit;
  padding: 0;

  &:hover {
    color: ${props => props.theme.button?.primary?.hover || '#0E7923'};
  }
`

// Success/Loading states
export const SuccessState = styled.div`
  text-align: center;
`

export const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  background: rgba(51, 170, 64, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;

  svg {
    width: 32px;
    height: 32px;
    color: ${props => props.theme.accent};
  }
`

export const SuccessTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: ${props => props.theme.label};
`

export const SuccessText = styled.p`
  color: ${props => props.theme.breadcrumb};
  margin-bottom: 1.5rem;
  line-height: 1.6;
`

export const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: ${props => props.theme.accent};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 0 auto;
`

export const EquanautPreview = styled.div`
  background: rgba(51, 170, 64, 0.1);
  border: 1px solid rgba(51, 170, 64, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`

export const EquanautPreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: ${props => props.theme.accent};
  font-weight: 600;
  font-size: 0.875rem;
`

export const EquanautPreviewText = styled.div`
  color: ${props => props.theme.breadcrumb};
  font-size: 0.875rem;
  line-height: 1.5;
`

// File upload styles
export const AttachButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: ${props => props.theme.disabled};
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    color: ${props => props.theme.accent};
    background: rgba(51, 170, 64, 0.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

export const FilePreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid ${props => props.theme.grayDivider};
  background: ${props => props.theme.selected === Themes.LIGHT
    ? 'rgba(0, 0, 0, 0.02)'
    : 'rgba(30, 40, 50, 0.3)'};
  border-radius: 0 0 8px 8px;
  margin-top: -1px;
`

export const FilePreviewChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.theme.selected === Themes.LIGHT
    ? 'rgba(0, 0, 0, 0.05)'
    : 'rgba(43, 59, 68, 0.8)'};
  border: 1px solid ${props => props.theme.grayDivider};
  border-radius: 4px;
  padding: 0.375rem 0.5rem 0.375rem 0.75rem;
  font-size: 0.8rem;
  color: ${props => props.theme.breadcrumb};
  max-width: 200px;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    color: ${props => props.theme.disabled};
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const FileRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: ${props => props.theme.disabled};
  cursor: pointer;
  padding: 0.125rem;
  border-radius: 2px;
  font-size: 1rem;
  line-height: 1;
  transition: all 0.2s ease;

  &:hover {
    color: #ff6b6b;
    background: rgba(255, 107, 107, 0.1);
  }
`

export const DragOverlay = styled.div<{ isDragging: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(51, 170, 64, 0.15);
  border: 2px dashed ${props => props.theme.accent};
  border-radius: 8px;
  display: ${props => props.isDragging ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.accent};
  font-weight: 600;
  font-size: 0.9rem;
  z-index: 10;
  pointer-events: none;
`

export const FileCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: rgba(51, 170, 64, 0.2);
  color: ${props => props.theme.accent};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-top: 0.5rem;
`

export const InputWrapperWithFiles = styled(InputWrapper)<{ hasFiles: boolean }>`
  ${props => props.hasFiles && `
    border-radius: 8px 8px 0 0;
  `}
`
